import { atom } from 'jotai'
import { unwrap } from 'jotai/utils'
import { currentPubkeyAtom, canWriteAtom, signerAtom } from './session'
import { relayListAtom, favoriteRelaysWithSetsAtom } from './lists'
import { loadRelayInfo } from '@nostr/gadgets/relays'
import { pool } from '@nostr/gadgets/global'
import { isHex32 } from '@nostr/gadgets/utils'
import { pointerFromATag } from '@/lib/tag'
import { premiumSubscribersChannelAtom, invalidatePremiumSubscribers } from './invalidation'
import { getDefaultStore } from 'jotai'
import { publish } from '@/services/client.service'
import { now } from '@/lib/utils'
import { toast } from 'sonner'
import i18n from 'i18next'

const store = getDefaultStore()
const t = i18n.t.bind(i18n)

export const PREMIUM_MEMBERSHIP_KIND = 1163

export const hasPremiumRelaysAtom = atom(async (get): Promise<string[]> => {
  const pubkey = get(currentPubkeyAtom)
  if (!pubkey) return []

  const [relayList, favorites] = await Promise.all([
    get(relayListAtom),
    get(favoriteRelaysWithSetsAtom)
  ])

  const urls: string[] = []
  for (const r of relayList) if (r.write) urls.push(r.url)
  for (const f of favorites) {
    if (typeof f === 'string') urls.push(f)
    else for (const u of f.items) urls.push(u)
  }
  const unique = Array.from(new Set(urls))

  const infos = await Promise.all(unique.map((url) => loadRelayInfo(url)))
  return unique.filter((_, i) => infos[i]?.supported_nips?.includes(63))
})
export const unwrappedHasPremiumRelaysAtom = unwrap(hasPremiumRelaysAtom)

export const isPremiumPublisherAtom = atom(async (get): Promise<boolean> => {
  if (!get(canWriteAtom)) return false
  const pubkey = get(currentPubkeyAtom)
  if (!pubkey) return false

  const premiumRelays = await get(hasPremiumRelaysAtom)
  if (premiumRelays.length === 0) return false

  const events = await pool.querySync(premiumRelays, {
    kinds: [PREMIUM_MEMBERSHIP_KIND],
    authors: [pubkey],
    limit: 1
  })
  return events.length > 0
})
export const unwrappedIsPremiumPublisherAtom = unwrap(isPremiumPublisherAtom)

export const premiumSubscribersAtom = atom(
  async (get): Promise<Map<string, 'direct' | 'delegated'>> => {
    get(premiumSubscribersChannelAtom)
    const pubkey = get(currentPubkeyAtom)
    if (!pubkey) return new Map()

    const premiumRelays = await get(hasPremiumRelaysAtom)
    if (premiumRelays.length === 0) return new Map()

    const events = await pool.querySync(premiumRelays, {
      kinds: [PREMIUM_MEMBERSHIP_KIND],
      authors: [pubkey]
    })

    const subscribers = new Map<string, 'direct' | 'delegated'>()
    const listPointers: ReturnType<typeof pointerFromATag>[] = []

    for (const event of events) {
      for (const tag of event.tags) {
        if (tag[0] === 'p' && isHex32(tag[1])) subscribers.set(tag[1], 'direct')
        else if (tag[0] === 'a') {
          const ptr = pointerFromATag(tag)
          if (ptr) listPointers.push(ptr)
        }
      }
    }

    // lists referenced via "a" tags must be sent directly to the relays
    // that hold the exclusive content, so we fetch them from the same relays
    for (const ptr of listPointers) {
      if (!ptr) continue
      const listEvents = await pool.querySync(premiumRelays, {
        kinds: [ptr.kind],
        authors: [ptr.pubkey],
        '#d': [ptr.identifier]
      })
      for (const ev of listEvents) {
        for (const tag of ev.tags) {
          if (tag[0] === 'p' && isHex32(tag[1])) {
            if (!subscribers.has(tag[1])) subscribers.set(tag[1], 'delegated')
          }
        }
      }
    }

    return subscribers
  }
)
export const unwrappedPremiumSubscribersAtom = unwrap(premiumSubscribersAtom)

export async function addPremiumSubscriber(targetPubkey: string): Promise<void> {
  const pubkey = store.get(currentPubkeyAtom)
  if (!pubkey) return
  const signer = await store.get(signerAtom)
  if (!signer) return

  const premiumRelays = await store.get(hasPremiumRelaysAtom)
  if (premiumRelays.length === 0) return

  const event = await publish(
    {
      kind: PREMIUM_MEMBERSHIP_KIND,
      content: '',
      tags: [['p', targetPubkey]],
      created_at: now()
    },
    { specifiedRelayUrls: premiumRelays }
  )
  if (!event) {
    toast.error(t('Failed to publish premium membership event.'))
    return
  }

  invalidatePremiumSubscribers()
}
